import { useEditorStore } from "components/Editor/useEditorStore";
import { useProjectSettingsContext } from "components/Settings/SettingsProvider";
import { useContainer } from "features/container/Feature";
import { useBranchEditor, useEditorFeature } from "features/editor/Feature";
import { useAddStatusNotification } from "features/notifications/hooks";
import { useSession } from "hooks/api/useSession";
import { useSetupProgress } from "hooks/pitcher/useSetupProgress";
import { hasBranchWriteAccess } from "isomorphic/permission";
import {
  Box,
  ButtonBadge,
  Badge,
  Popover,
  Paragraph,
  Tooltip,
} from "prism-react";

import { useGitHubScopes } from "hooks/useGitHubScopes";
import { canSeamlessBranch } from "utils/canSeamlessBranch";
import { executeVSCodeCommand } from "utils/vscode";

import { useEditorUpdateStatusContent } from "./useEditorUpdateStatusContent";

export const BranchStatus: React.FC = () => {
  const { git } = useEditorStore();
  const [editorFeature] = useEditorFeature();
  const editor = useBranchEditor();
  const editorUpdateStatus = useEditorUpdateStatusContent();

  const addStatusNotification = useAddStatusNotification();
  const [container] = useContainer();
  const { setVisibility } = useProjectSettingsContext();

  const gitHubScopes = useGitHubScopes();
  const setupProgress = useSetupProgress();
  const [session] = useSession();

  if (editorFeature.state === "READY" && editorFeature.isSeamlesslyBranching) {
    return (
      <Badge icon="progress" iconSize={4} variant="neutral">
        Seamlessly branching...
      </Badge>
    );
  }

  if (container.state === "RESTARTING") {
    return (
      <Tooltip content="The microVM is restarting and will shortly reconnect and run your setup tasks.">
        <div>
          <Badge icon="progress" iconSize={4} variant="neutral">
            Restarting
          </Badge>
        </div>
      </Tooltip>
    );
  }

  // We have been back and forward on when to show this badge. The reconnect can be slow for different reasons
  // and currently it is better to show it when you are disconnected or not, or the experience can be confusing. It
  // might be an idea to show some other visual indication to avoid catching attention, but at least now
  // the user is always aware of the state of the connection. For anonymous users we do not show it immediately as
  // they are more likely to be disconnected due to lack of sending requests to Pitcher
  if (
    (container.state === "RECONNECTING" ||
      container.state === "DISCONNECTED_RECONNECTING") &&
    (session.state === "AUTHENTICATED" || container.reconnectAttempts > 2)
  ) {
    return (
      <Badge icon="progress" iconSize={4} variant="neutral">
        Reconnecting
      </Badge>
    );
  }

  if (editorUpdateStatus) {
    return editorUpdateStatus;
  }

  if (git.state.status === "PULLING") {
    return (
      <ButtonBadge
        icon="progress"
        iconSize={4}
        /*
        We are not giving the shellId on pull notifications/request, but we can
        onClick={() => {
          dispatchLayout({
            type: "OPEN_TAB",
            tab: {
              type: "GIT_LOG",
            },
          });
        }}
        */
        variant="neutral"
      >
        Pull in progress
      </ButtonBadge>
    );
  }

  if (git.state.status === "COMMITTING" && git.state.shellId) {
    return (
      <ButtonBadge
        icon="progress"
        onClick={() => {
          executeVSCodeCommand("workbench.scm.focus");
        }}
        title="Toggle Git Terminal"
        variant="neutral"
      >
        Committing
      </ButtonBadge>
    );
  }

  if (git.state.status === "COMMIT_ERROR") {
    return (
      <ButtonBadge
        onClick={() => {
          executeVSCodeCommand("workbench.scm.focus");
        }}
        title="Toggle Git Terminal"
        variant="error"
      >
        Commit Error
      </ButtonBadge>
    );
  }

  if (setupProgress.state === "IN_PROGRESS") {
    return (
      <ButtonBadge
        icon="progress"
        iconSize={4}
        onClick={() => {
          executeVSCodeCommand("codesandbox.openCsbTab", {
            type: "SETUP_TASKS",
          });
        }}
        variant="neutral"
      >
        Setup in progress ({setupProgress.currentStepIndex + 1}/
        {setupProgress.steps.length})
      </ButtonBadge>
    );
  }

  if (setupProgress.state === "STOPPED") {
    return (
      <ButtonBadge
        iconSize={4}
        onClick={() => {
          executeVSCodeCommand("codesandbox.openCsbTab", {
            type: "SETUP_TASKS",
          });
        }}
        variant="error"
      >
        Setup failed ({setupProgress.currentStepIndex + 1}/
        {setupProgress.steps.length})
      </ButtonBadge>
    );
  }

  if (
    !hasBranchWriteAccess(editor.authorization) &&
    !gitHubScopes?.publicRepos &&
    !canSeamlessBranch(editor)
  ) {
    return (
      <Popover
        trigger={
          <ButtonBadge icon="eyeOpen" iconSize={4} variant="accentSecondary">
            Read only
          </ButtonBadge>
        }
      >
        <Box css={{ width: 300 }}>
          <Paragraph>
            CodeSandbox needs access to your GitHub repositories data. Update
            your permissions to access the full experience.
          </Paragraph>
        </Box>
      </Popover>
    );
  }

  const hasWriteAccess = hasBranchWriteAccess(editor.authorization);

  if (!hasWriteAccess && !canSeamlessBranch(editor)) {
    return (
      <Popover
        trigger={
          <ButtonBadge icon="eyeOpen" iconSize={4} variant="accentSecondary">
            Read only
          </ButtonBadge>
        }
      >
        <Box css={{ width: 210 }}>
          <Paragraph>
            You only have read access to this repository, fork to get access
          </Paragraph>
        </Box>
      </Popover>
    );
  }

  if (editor.protectedBranch) {
    return (
      <ButtonBadge
        icon="lockClose"
        onClick={() => {
          setVisibility(true);
        }}
        title="Protected branches do not allow file changes and git operations"
        variant="accentSecondary"
      >
        Protected
      </ButtonBadge>
    );
  }

  if (git.status.conflicts) {
    return (
      <ButtonBadge
        onClick={() => {
          executeVSCodeCommand("workbench.scm.focus");
        }}
        title="Show conflicted files"
        variant="error"
      >
        Conflicts
      </ButtonBadge>
    );
  }

  if (!git.status.conflicts && git.status.isMerging) {
    return (
      <ButtonBadge
        onClick={() => {
          executeVSCodeCommand("workbench.scm.focus");
        }}
      >
        Merge commit pending
      </ButtonBadge>
    );
  }

  if (git.status.remote.behind > 0) {
    const badgeText = `${git.status.remote.behind} ${
      git.status.remote.behind === 1 ? "commit" : "commits"
    } behind remote branch`;

    if (git.status.localChanges) {
      return (
        <ButtonBadge
          icon="circleBang"
          iconColor="informative-error-base"
          title="Commit changes before pulling remote"
          disabled
        >
          {badgeText}
        </ButtonBadge>
      );
    }

    const safePull = git.status.remote.safe;

    return (
      <ButtonBadge
        icon={safePull ? undefined : "circleBang"}
        iconColor={safePull ? undefined : "informative-error-base"}
        onClick={() => {
          if (git.status.localChanges) {
            addStatusNotification({
              status: "warning",
              message: `To merge from remote, you first need to commit your local changes`,
            });
          } else {
            git.pullRemote();
          }
        }}
        title={safePull ? "Pull remote" : "Pull may generate conflicts"}
      >
        {badgeText}
      </ButtonBadge>
    );
  }

  if (git.status.target.branch && git.status.target.behind > 0) {
    const badgeText = `${git.status.target.behind} ${
      git.status.target.behind === 1 ? "commit" : "commits"
    } behind ${git.targetBranchName}`;

    if (git.status.localChanges) {
      return (
        <ButtonBadge
          icon="circleBang"
          iconColor="informative-error-base"
          title={`Commit changes before pulling ${git.targetBranchName}`}
          disabled
        >
          {badgeText}
        </ButtonBadge>
      );
    }

    const safePull = git.status.target.safe;

    return (
      <ButtonBadge
        icon={safePull ? undefined : "circleBang"}
        iconColor={safePull ? undefined : "informative-error-base"}
        onClick={() => {
          if (git.status.localChanges) {
            addStatusNotification({
              status: "warning",
              message: `To merge from ${git.targetBranchName}, you first need to commit your local changes`,
            });
          } else {
            git.pullTarget();
          }
        }}
        title={
          safePull
            ? `Pull ${git.targetBranchName} into current branch`
            : `Pulling ${git.targetBranchName} into current branch may generate conflicts`
        }
      >
        {badgeText}
      </ButtonBadge>
    );
  }

  return null;
};
